import { useCallback, useEffect, useRef, useState } from "react";

/**
 * How long a field waits, after the typing stops, before it treats the person
 * as done.
 *
 * Measured from the LAST keystroke, not the first, so it only elapses once
 * someone has actually paused — which is why it can be this short without
 * interrupting anyone mid-word.
 *
 * One constant because two things key off the same moment and would be jarring
 * apart: `FormItem` revealing an error, and a control opening its warning
 * explanation.
 */
export const DONE_TYPING_AFTER_MS = 2000;

/**
 * Whether the typing has settled. Starts `true` — a field that arrives already
 * warning shouldn't stay quiet until someone types in it.
 *
 * The motivating case: `minSizeWarning` on a height field. Typing `22` passes
 * through `2`, which trips the threshold, so anything keyed to the value alone
 * flashes a warning between two keystrokes.
 */
export function useTypingPause() {
  const [settled, setSettled] = useState(true);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const noteKeystroke = useCallback(() => {
    setSettled(false);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setSettled(true), DONE_TYPING_AFTER_MS);
  }, []);

  return { settled, noteKeystroke };
}
